import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ApiRequestsService } from '../../services/api-requests.service';

interface DocType {
  _id: string;
  documentName: string;
  documentPeriod: 'Monthly' | 'Yearly' | 'One time';
  documentFrequency: string;
  authorizedEmpNos: string[];
}

interface Employee {
  _id: string;
  empNo: string;
  name: string;
}

@Component({
  selector: 'app-upload-documents',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './upload-documents.component.html',
  styleUrl: './upload-documents.component.scss',
})
export class UploadDocumentsComponent implements OnInit {
  docTypes: DocType[] = [];
  employees: Employee[] = [];

  selectedDocType: DocType | null = null;
  selectedEmpNo = '';
  forMonth = '';
  forYear = '';
  selectedFile: File | null = null;

  isUploading = false;
  uploadMessage = '';
  uploadSuccess = false;

  readonly months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December',
  ];

  readonly years: string[] = (() => {
    const y = new Date().getFullYear();
    return Array.from({ length: 6 }, (_, i) => String(y - i));
  })();

  constructor(private apiService: ApiRequestsService) {}

  ngOnInit(): void {
    this.apiService.getApiCall('api/v1/documenttypemaster/list').subscribe({
      next: (res: any) => {
        if (res?.errorCode === this.apiService.NO_ERROR) this.docTypes = res.Data || [];
      },
    });
    this.apiService.getApiCall('api/v1/employeemaster/list').subscribe({
      next: (res: any) => {
        if (res?.errorCode === this.apiService.NO_ERROR) this.employees = res.Data || [];
      },
    });
  }

  get selectedEmployee(): Employee | undefined {
    return this.employees.find(e => e.empNo === this.selectedEmpNo);
  }

  get isConditionalAccess(): boolean {
    return this.selectedDocType?.documentFrequency === 'Conditional Access';
  }

  onDocTypeChange(): void {
    this.forMonth = '';
    this.forYear = '';
    this.selectedEmpNo = '';
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0] ?? null;
    this.uploadMessage = '';
    if (file && file.type !== 'application/pdf' && !file.name.toLowerCase().endsWith('.pdf')) {
      this.selectedFile = null;
      input.value = '';
      this.uploadMessage = 'Only PDF files are accepted.';
      this.uploadSuccess = false;
      return;
    }
    this.selectedFile = file;
  }

  get isMonthly(): boolean {
    return this.selectedDocType?.documentPeriod === 'Monthly';
  }

  get isCommon(): boolean {
    return this.selectedDocType?.documentFrequency === 'Common';
  }

  get isFormValid(): boolean {
    if (!this.selectedDocType || !this.selectedFile) return false;
    const period = this.selectedDocType.documentPeriod;

    if (this.isConditionalAccess) {
      if (!this.selectedDocType.authorizedEmpNos.length) return false;
      if (period === 'Monthly') return !!(this.forMonth && this.forYear);
      if (period === 'Yearly') return !!this.forYear;
      return true; // One time
    }

    if (this.isCommon) {
      if (period === 'Monthly') return !!(this.forMonth && this.forYear);
      if (period === 'Yearly') return !!this.forYear;
      return true; // One time
    }

    if (this.isMonthly) return true; // Individual Monthly = bulk payslip, no period fields

    // Individual non-Monthly
    if (!this.selectedEmpNo) return false;
    if (period === 'Yearly') return !!this.forYear;
    return true; // One time
  }

  upload(): void {
    if (!this.isFormValid || !this.selectedFile || !this.selectedDocType) return;

    this.isUploading = true;
    this.uploadMessage = '';

    if (this.isCommon) {
      // Common document — no employee association; any employee can access on WhatsApp.
      const criteria: any = {
        documentTypeName: this.selectedDocType.documentName,
        forYear: this.forYear || null,
        forMonth: this.forMonth || null,
      };
      this.apiService.uploadFile(this.selectedFile, 'api/v1/documentupload/upload/uploadDocument', criteria).subscribe({
        next: (res: any) => {
          this.isUploading = false;
          if (res?.errorCode === this.apiService.NO_ERROR) {
            this.uploadMessage = 'Document uploaded successfully.';
            this.uploadSuccess = true;
            this.resetForm();
          } else {
            this.uploadMessage = res?.errorDescription || 'Upload failed. Please try again.';
            this.uploadSuccess = false;
          }
        },
        error: () => {
          this.isUploading = false;
          this.uploadMessage = 'Upload failed. Please check your connection.';
          this.uploadSuccess = false;
        },
      });
    } else if (this.isMonthly) {
      // Bulk upload — the backend exe splits the PDF per employee.
      this.apiService.uploadFile(this.selectedFile, 'api/v1/payslip/upload/uploadPayslip').subscribe({
        next: (res: any) => {
          this.isUploading = false;
          if (res?.errorCode === this.apiService.NO_ERROR) {
            const { insertedCount = 0, skippedCount = 0 } = res.Data || {};
            this.uploadMessage = `Processed successfully — ${insertedCount} added, ${skippedCount} skipped.`;
            this.uploadSuccess = true;
            this.resetForm();
          } else {
            this.uploadMessage = res?.errorDescription || 'Upload failed. Please try again.';
            this.uploadSuccess = false;
          }
        },
        error: () => {
          this.isUploading = false;
          this.uploadMessage = 'Upload failed. Please check your connection.';
          this.uploadSuccess = false;
        },
      });
    } else if (this.isConditionalAccess) {
      const criteria: any = {
        documentTypeName: this.selectedDocType.documentName,
        empNos: this.selectedDocType.authorizedEmpNos,
        forYear: this.forYear || null,
        forMonth: this.forMonth || null,
      };

      this.apiService.uploadFile(this.selectedFile, 'api/v1/documentupload/upload/uploadDocument', criteria).subscribe({
        next: (res: any) => {
          this.isUploading = false;
          if (res?.errorCode === this.apiService.NO_ERROR) {
            const count = res.Data?.insertedCount ?? this.selectedDocType?.authorizedEmpNos.length ?? 0;
            this.uploadMessage = `Document uploaded and granted to ${count} employee(s).`;
            this.uploadSuccess = true;
            this.resetForm();
          } else {
            this.uploadMessage = res?.errorDescription || 'Upload failed. Please try again.';
            this.uploadSuccess = false;
          }
        },
        error: () => {
          this.isUploading = false;
          this.uploadMessage = 'Upload failed. Please check your connection.';
          this.uploadSuccess = false;
        },
      });
    } else {
      const emp = this.selectedEmployee;
      const criteria: any = {
        documentTypeName: this.selectedDocType.documentName,
        empNo: this.selectedEmpNo,
        employeeName: emp?.name || '',
      };
      if (this.selectedDocType.documentPeriod === 'Yearly') {
        criteria['forYear'] = this.forYear;
      }

      this.apiService.uploadFile(this.selectedFile, 'api/v1/documentupload/upload/uploadDocument', criteria).subscribe({
        next: (res: any) => {
          this.isUploading = false;
          if (res?.errorCode === this.apiService.NO_ERROR) {
            this.uploadMessage = 'Document uploaded successfully.';
            this.uploadSuccess = true;
            this.resetForm();
          } else {
            this.uploadMessage = res?.errorDescription || 'Upload failed. Please try again.';
            this.uploadSuccess = false;
          }
        },
        error: () => {
          this.isUploading = false;
          this.uploadMessage = 'Upload failed. Please check your connection.';
          this.uploadSuccess = false;
        },
      });
    }
  }

  private resetForm(): void {
    this.selectedDocType = null;
    this.selectedEmpNo = '';
    this.forMonth = '';
    this.forYear = '';
    this.selectedFile = null;
  }
}
